import React, { useState, useEffect, useRef } from 'react';
import { api } from '../App';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { MapPin } from 'lucide-react';

/**
 * Cascading District → Taluk → Hobli → Village picker with per-field manual fallback.
 * `value` = {district, taluk, hobli, village} (name strings).
 * `onChange(nextValue)` is called whenever any field changes.
 *
 * Hydration runs whenever `value` changes to names the component hasn't
 * already resolved to ids. Names that don't match a Karnataka option
 * automatically flip that field to Manual so the user always sees the
 * auto-filled value rather than an empty dropdown.
 */
const CaseLocationFields = ({ value, onChange, testIdPrefix = 'case-location', showHeader = true }) => {
  const val = value || {};
  const [districts, setDistricts] = useState([]);
  const [taluks, setTaluks] = useState([]);
  const [hoblis, setHoblis] = useState([]);
  const [villages, setVillages] = useState([]);
  const [ids, setIds] = useState({ district: '', taluk: '', hobli: '', village: '' });
  const [manual, setManual] = useState({ district: false, taluk: false, hobli: false, village: false });

  // Track the last name-snapshot we successfully hydrated so we don't re-run on every render
  const lastHydratedRef = useRef({ district: null, taluk: null, hobli: null, village: null });

  // Load districts once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/locations?level=district');
        if (!cancelled) setDistricts(res.data || []);
      } catch (e) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Re-hydrate whenever `value` names change (e.g., parent loaded case data asynchronously)
  useEffect(() => {
    if (districts.length === 0) return;
    const snapshot = {
      district: val.district || '',
      taluk: val.taluk || '',
      hobli: val.hobli || '',
      village: val.village || '',
    };
    const last = lastHydratedRef.current;
    if (
      snapshot.district === last.district &&
      snapshot.taluk === last.taluk &&
      snapshot.hobli === last.hobli &&
      snapshot.village === last.village
    ) {
      return;
    }
    lastHydratedRef.current = snapshot;

    let cancelled = false;
    (async () => {
      const nextIds = { district: '', taluk: '', hobli: '', village: '' };
      const nextManual = { district: false, taluk: false, hobli: false, village: false };
      let talukList = [];
      let hobliList = [];
      let villageList = [];

      if (snapshot.district) {
        const d = districts.find(x => x.name === snapshot.district);
        if (d) {
          nextIds.district = d.id;
          try {
            const res = await api.get(`/locations?level=taluk&parent_id=${d.id}`);
            talukList = res.data || [];
          } catch (e) { /* ignore */ }
        } else {
          nextManual.district = true;
          if (snapshot.taluk) nextManual.taluk = true;
          if (snapshot.hobli) nextManual.hobli = true;
          if (snapshot.village) nextManual.village = true;
        }
      }

      if (snapshot.taluk && !nextManual.taluk && talukList.length > 0) {
        const t = talukList.find(x => x.name === snapshot.taluk);
        if (t) {
          nextIds.taluk = t.id;
          try {
            const res = await api.get(`/locations?level=hobli&parent_id=${t.id}`);
            hobliList = res.data || [];
          } catch (e) { /* ignore */ }
        } else {
          nextManual.taluk = true;
          if (snapshot.hobli) nextManual.hobli = true;
          if (snapshot.village) nextManual.village = true;
        }
      } else if (snapshot.taluk && !nextManual.taluk) {
        nextManual.taluk = true;
      }

      if (snapshot.hobli && !nextManual.hobli && hobliList.length > 0) {
        const h = hobliList.find(x => x.name === snapshot.hobli);
        if (h) {
          nextIds.hobli = h.id;
          try {
            const res = await api.get(`/locations?level=village&parent_id=${h.id}`);
            villageList = res.data || [];
          } catch (e) { /* ignore */ }
        } else {
          nextManual.hobli = true;
          if (snapshot.village) nextManual.village = true;
        }
      } else if (snapshot.hobli && !nextManual.hobli) {
        nextManual.hobli = true;
      }

      if (snapshot.village && !nextManual.village && villageList.length > 0) {
        const v = villageList.find(x => x.name === snapshot.village);
        if (v) nextIds.village = v.id;
        else nextManual.village = true;
      } else if (snapshot.village && !nextManual.village) {
        nextManual.village = true;
      }

      if (cancelled) return;
      setTaluks(talukList);
      setHoblis(hobliList);
      setVillages(villageList);
      setManual(prev => ({ ...prev, ...nextManual }));
      // Defer ids to next tick so Radix Select sees SelectItems before the value prop changes
      await new Promise((resolve) => setTimeout(resolve, 0));
      if (cancelled) return;
      setIds(nextIds);
    })();
    return () => { cancelled = true; };
  }, [val.district, val.taluk, val.hobli, val.village, districts]);

  const update = (patch) => onChange({ ...val, ...patch });

  const handleDistrict = async (id) => {
    if (!id) return; // Ignore spurious reset events from Radix
    const d = districts.find(x => x.id === id);
    if (!d) return;
    setIds({ district: id, taluk: '', hobli: '', village: '' });
    setTaluks([]); setHoblis([]); setVillages([]);
    const next = { ...val, district: d.name, taluk: '', hobli: '', village: '' };
    lastHydratedRef.current = { district: next.district, taluk: '', hobli: '', village: '' };
    onChange(next);
    try {
      const res = await api.get(`/locations?level=taluk&parent_id=${id}`);
      setTaluks(res.data || []);
    } catch (e) { /* ignore */ }
  };

  const handleTaluk = async (id) => {
    if (!id) return;
    const t = taluks.find(x => x.id === id);
    if (!t) return;
    setIds(p => ({ ...p, taluk: id, hobli: '', village: '' }));
    setHoblis([]); setVillages([]);
    const next = { ...val, taluk: t.name, hobli: '', village: '' };
    lastHydratedRef.current = { ...lastHydratedRef.current, taluk: next.taluk, hobli: '', village: '' };
    onChange(next);
    try {
      const res = await api.get(`/locations?level=hobli&parent_id=${id}`);
      setHoblis(res.data || []);
    } catch (e) { /* ignore */ }
  };

  const handleHobli = async (id) => {
    if (!id) return;
    const h = hoblis.find(x => x.id === id);
    if (!h) return;
    setIds(p => ({ ...p, hobli: id, village: '' }));
    setVillages([]);
    const next = { ...val, hobli: h.name, village: '' };
    lastHydratedRef.current = { ...lastHydratedRef.current, hobli: next.hobli, village: '' };
    onChange(next);
    try {
      const res = await api.get(`/locations?level=village&parent_id=${id}`);
      setVillages(res.data || []);
    } catch (e) { /* ignore */ }
  };

  const handleVillage = (id) => {
    if (!id) return;
    const v = villages.find(x => x.id === id);
    if (!v) return;
    setIds(p => ({ ...p, village: id }));
    const next = { ...val, village: v.name };
    lastHydratedRef.current = { ...lastHydratedRef.current, village: next.village };
    onChange(next);
  };

  const toggleManual = (level) => {
    setManual(prev => {
      const next = { ...prev, [level]: !prev[level] };
      if (next[level]) setIds(p => ({ ...p, [level]: '' }));
      return next;
    });
  };

  const renderLevel = (level, label, items, selectedId, onSelect, disabled = false, disabledPlaceholder = '') => (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        <div className="flex items-center gap-1">
          <Checkbox
            id={`${testIdPrefix}-manual-${level}`}
            checked={manual[level]}
            onCheckedChange={() => toggleManual(level)}
          />
          <label htmlFor={`${testIdPrefix}-manual-${level}`} className="text-xs text-slate-500">Manual</label>
        </div>
      </div>
      {manual[level] ? (
        <Input
          value={val[level] || ''}
          onChange={(e) => update({ [level]: e.target.value })}
          placeholder={`Enter ${label}`}
          data-testid={`${testIdPrefix}-${level}`}
        />
      ) : (
        <Select value={selectedId} onValueChange={onSelect} disabled={disabled && items.length === 0}>
          <SelectTrigger data-testid={`${testIdPrefix}-${level}`}>
            <SelectValue placeholder={disabled && items.length === 0 ? disabledPlaceholder : `Select ${label}`} />
          </SelectTrigger>
          <SelectContent>
            {items.length === 0 ? (
              <div className="px-2 py-1.5 text-xs text-slate-400">No {label.toLowerCase()}s — use Manual</div>
            ) : (
              items.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)
            )}
          </SelectContent>
        </Select>
      )}
    </div>
  );

  return (
    <div className="space-y-3">
      {showHeader && (
        <div className="flex items-center gap-2 text-sm font-semibold text-[#1e3a5f]">
          <MapPin size={16} />
          Property Location
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {renderLevel('district', 'District', districts, ids.district, handleDistrict)}
        {renderLevel('taluk', 'Taluk', taluks, ids.taluk, handleTaluk, true, 'Select District first')}
        {renderLevel('hobli', 'Hobli', hoblis, ids.hobli, handleHobli, true, 'Select Taluk first')}
        {renderLevel('village', 'Village', villages, ids.village, handleVillage, true, 'Select Hobli first')}
      </div>
    </div>
  );
};

export default CaseLocationFields;
